import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { DailyChecklistItem } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, X, Loader2, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { uploadChecklistProof } from "@/lib/upload";
import { SignaturePad, type SignatureCapture } from "./signature-pad";

// Phase 42 Plan 14 (TPRF-01/02) — the proof-capture dialog that gates the
// doer's tick. 42-09 made the server refuse an unproven tick (422
// proof_required, task-checklist-items.ts's ProofPayloadWithType); this
// dialog is how the doer actually attaches the evidence BEFORE the tick is
// sent — the disabled submit button is the UX, the server 422 is the
// backstop, never the other way round.
//
// Reuses the EXISTING uploadChecklistProof() (Supabase Storage, 42-09) for
// both photo and signature-PNG uploads — no new storage client anywhere in
// this file, only the shared upload helper import above.
//
// Plain markup throughout (local component state only, no react-hook-form)
// — shadcn Form* primitives require a <FormField> ancestor and would
// white-screen this dialog otherwise (the standing repo trap, latest
// 0e61073).

export interface ProofPayload {
  proofUrls?: string[];
  proofNote?: string;
  signatureUrl?: string;
  signerName?: string;
}

interface PhotoSlot {
  id: string;
  previewUrl: string;
  status: "uploading" | "done" | "error";
  url?: string;
}

const MAX_PHOTOS = 5;

export function ProofCaptureDialog({
  open,
  onOpenChange,
  item,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: Pick<DailyChecklistItem, "id" | "label" | "proofType">;
  /** Parent owns the actual tick mutation. A rejected promise means the 422
   *  backstop fired (or the network failed) — this dialog stays open and
   *  every already-uploaded URL/note/signature stays in local state below,
   *  so retry is one tap, never re-shooting photos. */
  onSubmit: (proof: ProofPayload) => Promise<void>;
  submitting?: boolean;
}) {
  const proofType = item.proofType ?? "none";
  const [photos, setPhotos] = useState<PhotoSlot[]>([]);
  const [note, setNote] = useState("");
  const [signature, setSignature] = useState<SignatureCapture | null>(null);

  // Fresh local state on every genuinely NEW open (a different item, or the
  // first attempt on this item) — but deliberately NOT on a 422 retry, where
  // `open` stays true the whole time (the parent never toggles it on error)
  // and the already-uploaded evidence must survive untouched.
  useEffect(() => {
    if (open) {
      setPhotos([]);
      setNote("");
      setSignature(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item.id]);

  function addPhotoSlot(previewUrl: string): string {
    const id = `${Date.now()}-${Math.random()}`;
    setPhotos((prev) => [...prev, { id, previewUrl, status: "uploading" }]);
    return id;
  }

  function patchPhoto(id: string, patch: Partial<PhotoSlot>) {
    setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }

  async function handlePhotoSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting/re-shooting the same filename next tap
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) {
      toast.error(`Up to ${MAX_PHOTOS} photos.`);
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    const id = addPhotoSlot(previewUrl);
    try {
      // uploadChecklistProof compresses client-side before posting (TCHK-04
      // — housekeeping shoot these on low-end Androids over patchy wifi; an
      // uncompressed 8MP photo is a failed tick). No new storage.
      const result = await uploadChecklistProof(file);
      if (result.ok) {
        patchPhoto(id, { status: "done", url: result.url });
      } else {
        patchPhoto(id, { status: "error" });
        toast.error(result.message || "Photo upload failed.");
      }
    } catch {
      patchPhoto(id, { status: "error" });
      toast.error("Photo upload failed — check your connection and try again.");
    }
  }

  function removePhoto(id: string) {
    setPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  }

  const uploadedPhotoUrls = photos.filter((p) => p.status === "done" && p.url).map((p) => p.url as string);
  const anyPhotoUploading = photos.some((p) => p.status === "uploading");

  const canSubmit = (() => {
    if (submitting) return false;
    if (proofType === "photo") return uploadedPhotoUrls.length > 0 && !anyPhotoUploading;
    if (proofType === "note") return note.trim().length > 0;
    if (proofType === "signature") return signature != null;
    return true; // "none" never opens this dialog (checklist-daily-tab.tsx's gate) — kept honest, not reachable
  })();

  async function handleSubmit() {
    if (!canSubmit) return;
    const proof: ProofPayload =
      proofType === "photo"
        ? { proofUrls: uploadedPhotoUrls }
        : proofType === "note"
          ? { proofNote: note.trim() }
          : proofType === "signature" && signature
            ? { signatureUrl: signature.signatureUrl, signerName: signature.signerName }
            : {};
    try {
      await onSubmit(proof);
    } catch {
      // The parent already surfaced a toast (checklist-daily-tab.tsx's
      // handleProofSubmit) and intentionally leaves this dialog open on
      // failure — swallow here so this isn't also an unhandled rejection.
    }
  }

  const title =
    proofType === "photo" ? "Attach photo proof" :
    proofType === "note" ? "Add a note" :
    proofType === "signature" ? "Client signature" :
    "Mark done";

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            &ldquo;{item.label}&rdquo; needs proof attached before it can be marked done.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {proofType === "photo" && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {photos.map((p) => (
                  <div key={p.id} className="relative h-20 w-20 overflow-hidden rounded-md border">
                    <img src={p.previewUrl} alt="" className="h-full w-full object-cover" />
                    {p.status === "uploading" && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <Loader2 className="h-5 w-5 animate-spin text-white" />
                      </div>
                    )}
                    {p.status === "error" && (
                      <div className="absolute inset-0 flex items-center justify-center bg-destructive/70">
                        <span className="text-[10px] text-white">Failed</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(p.id)}
                      className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white"
                      aria-label="Remove photo"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                  <label
                    htmlFor="proof-photo-input"
                    className={cn(
                      "flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <Camera className="h-5 w-5" />
                    <span className="text-[10px]">Add photo</span>
                  </label>
                )}
              </div>
              {/* capture="environment" opens the phone camera directly — a tap saved on every round (TCHK-04). */}
              <input
                id="proof-photo-input"
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handlePhotoSelected}
              />
              <p className="text-xs text-muted-foreground">
                {photos.length}/{MAX_PHOTOS} photos · compressed automatically before upload.
              </p>
            </div>
          )}

          {proofType === "note" && (
            <div className="space-y-1">
              <label htmlFor="proof-note" className="text-xs font-medium text-muted-foreground">Note</label>
              <Textarea
                id="proof-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What did you observe / do?"
                rows={4}
                autoFocus
              />
            </div>
          )}

          {proofType === "signature" && (
            signature ? (
              <div className="flex items-center justify-between rounded-md border p-3">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span>Signed by <span className="font-medium">{signature.signerName}</span></span>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => setSignature(null)} disabled={submitting}>
                  Redo
                </Button>
              </div>
            ) : (
              <SignaturePad onCaptured={setSignature} disabled={submitting} />
            )
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? "Marking done…" : "Mark done"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
